import React, { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

/**
 * Admin-only bulk hard-delete with type-DELETE confirmation.
 *
 * Props:
 *  - selectedIds: array of selected entity IDs
 *  - resource: human label, plural ("products", "wooden crates", "shipments")
 *  - endpoint: backend POST URL, e.g. "/products/bulk-delete"
 *  - onDeleted: callback after successful delete (parent clears selection + reloads)
 *  - testIdRoot: prefix for data-testid (e.g. "products" -> "products-bulk-delete-btn")
 */
export default function BulkDeleteButton({ selectedIds, resource, endpoint, onDeleted, testIdRoot }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const n = selectedIds?.length || 0;
  const canConfirm = confirmText.trim().toUpperCase() === "DELETE" && !deleting && n > 0;

  function closeAll() {
    setOpen(false);
    setConfirmText("");
    setError(null);
  }

  async function doDelete() {
    if (!canConfirm) return;
    setDeleting(true);
    setError(null);
    try {
      const { data } = await api.post(endpoint, { ids: selectedIds });
      closeAll();
      onDeleted && onDeleted(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={n === 0}
        data-testid={`${testIdRoot}-bulk-delete-btn`}
      >
        <Trash2 className="mr-1 h-3.5 w-3.5" />
        Delete selected ({n})
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!o) closeAll(); else setOpen(true); }}>
        <DialogContent data-testid={`${testIdRoot}-bulk-delete-dialog`}>
          <DialogHeader>
            <DialogTitle className="text-rose-700">Permanently delete {n} {resource}?</DialogTitle>
            <DialogDescription>
              This is a hard delete — the {resource} and any dependent records (history, related shipments / tickets, etc.) will be removed from the database. <span className="font-medium text-slate-900">This cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Type <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-rose-700">DELETE</span> to confirm
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
              data-testid={`${testIdRoot}-bulk-delete-confirm-input`}
            />
          </div>
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={closeAll} disabled={deleting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={doDelete}
              disabled={!canConfirm}
              data-testid={`${testIdRoot}-bulk-delete-confirm`}
            >
              {deleting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Yes, permanently delete {n}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
